import { cidrZ, MAX_ALLOWED_IPS } from "@agentcall/core/type/admin_organization_ip_restriction_type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { Plus, Trash2 } from "react-feather"
import { useFieldArray, useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import * as z from "zod"
import { organizationApi } from "@/api/client/organization"
import type { OrganizationIpRestrictionGet200IpRestriction } from "@/api/generated/models"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "@/hooks/use-toast"
import { EnableIpRestrictionModal } from "./EnableIpRestrictionModal"

const ipRestrictionFormSchema = z
  .object({
    enabled: z.boolean(),
    allowedIps: z
      .array(
        z.object({
          cidr: z
            .string()
            .trim()
            .min(1, "IPアドレスまたはCIDRを入力してください")
            .refine(
              (value) => cidrZ.safeParse(value).success,
              "IPアドレスまたはCIDR形式で入力してください（例: 203.0.113.0/24）"
            ),
          description: z.string().max(255, "説明は255文字以内で入力してください"),
        })
      )
      .max(MAX_ALLOWED_IPS, `許可するIPアドレスは${MAX_ALLOWED_IPS}件まで登録できます`),
  })
  .refine((values) => !values.enabled || values.allowedIps.length > 0, {
    message: "IPアドレス制限を有効にするには、許可するIPアドレスを1件以上登録してください",
    path: ["allowedIps"],
  })

type IpRestrictionFormValues = z.infer<typeof ipRestrictionFormSchema>

// サーバーは単体のIPアドレスも 203.0.113.1/32 のようにプレフィックス付きで保存するため、
// 保存後の値と比べられるよう両方の表記を候補にする。
const currentIpNotations = (currentIp: string): string[] => [
  currentIp,
  `${currentIp}/${currentIp.includes(":") ? 128 : 32}`,
]

export function IpRestrictionForm({
  organizationName,
  ipRestriction,
  currentIp,
  isOwnOrganization,
  canEdit,
  onSaved,
}: {
  organizationName: string
  ipRestriction: OrganizationIpRestrictionGet200IpRestriction
  currentIp: string | null
  isOwnOrganization: boolean
  canEdit: boolean
  onSaved: () => void
}) {
  const navigate = useNavigate()
  const [confirmingValues, setConfirmingValues] = useState<IpRestrictionFormValues | null>(null)

  const form = useForm<IpRestrictionFormValues>({
    resolver: zodResolver(ipRestrictionFormSchema),
    defaultValues: {
      enabled: ipRestriction.enabled,
      allowedIps: ipRestriction.allowedIps.map(({ cidr, description }) => ({ cidr, description })),
    },
  })
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "allowedIps" })
  const allowedIps = form.watch("allowedIps")

  const updateIpRestriction = organizationApi.useOrganizationIpRestrictionUpdate()

  const save = (values: IpRestrictionFormValues) => {
    updateIpRestriction.mutate(
      { id: ipRestriction.organizationId, data: values },
      {
        onSuccess: () => {
          toast({ title: "IPアドレス制限を保存しました" })
          setConfirmingValues(null)
          onSaved()
        },
        // 呼び出し時の onError なので、失敗の理由は共通のトーストが出す。
        onError: () => setConfirmingValues(null),
      }
    )
  }

  const onSubmit = (values: IpRestrictionFormValues) => {
    // 保存済みの設定が無効から有効に変わると管理者が締め出され得るため、このときだけ確認する。
    if (values.enabled && !ipRestriction.enabled) {
      setConfirmingValues(values)
      return
    }
    save(values)
  }

  const isFull = fields.length >= MAX_ALLOWED_IPS
  const isCurrentIpListed =
    currentIp !== null &&
    allowedIps.some((allowedIp) => currentIpNotations(currentIp).includes(allowedIp.cidr.trim()))
  const listError =
    form.formState.errors.allowedIps?.message ?? form.formState.errors.allowedIps?.root?.message

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-md p-6 space-y-4">
          <FormField
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    label="IPアドレス制限を有効にする"
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                    disabled={!canEdit}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <p className="text-sm text-slate-500">
            有効にすると、この組織の管理者は下の許可リストに含まれるIPアドレスからのみログイン・操作できます。システム管理者も対象です。
          </p>
          {isOwnOrganization && (
            <Alert>
              <AlertDescription>
                自分が所属する組織です。現在のIPアドレス（{currentIp ?? "取得できません"}
                ）を含まない設定で有効にすると自分もアクセスできなくなるため、保存できません。
              </AlertDescription>
            </Alert>
          )}
        </div>

        <div className="bg-white rounded-md p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-bold">許可するIPアドレス</h2>
            {canEdit && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => currentIp && append({ cidr: currentIp, description: "" })}
                  disabled={currentIp === null || isCurrentIpListed || isFull}
                >
                  現在のIPアドレス（{currentIp ?? "取得できません"}）を追加
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => append({ cidr: "", description: "" })}
                  disabled={isFull}
                >
                  <Plus className="mr-2" size={16} />
                  行を追加
                </Button>
              </div>
            )}
          </div>

          <p className="text-sm text-slate-500">
            1件ずつIPアドレス（例: 198.51.100.17）またはCIDR（例:
            203.0.113.0/24）で入力します。保存時にネットワークアドレスへ揃えます。最大
            {MAX_ALLOWED_IPS}件まで登録できます。
          </p>

          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              許可するIPアドレスが登録されていません
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2 text-xs font-semibold text-slate-600">
                <span className="w-64">IPアドレス / CIDR</span>
                <span className="flex-1">説明</span>
                {canEdit && <span className="w-9" />}
              </div>
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-start gap-2">
                  <FormField
                    control={form.control}
                    name={`allowedIps.${index}.cidr`}
                    render={({ field: cidrField }) => (
                      <FormItem className="w-64">
                        <FormControl>
                          <Input
                            aria-label={`IPアドレス（${index + 1}行目）`}
                            placeholder="203.0.113.0/24"
                            disabled={!canEdit}
                            {...cidrField}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`allowedIps.${index}.description`}
                    render={({ field: descriptionField }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input
                            aria-label={`説明（${index + 1}行目）`}
                            placeholder="本社オフィス"
                            disabled={!canEdit}
                            {...descriptionField}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {canEdit && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`${index + 1}行目を削除`}
                      onClick={() => remove(index)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {listError && <p className="text-sm font-medium text-destructive">{listError}</p>}
        </div>

        <div className="flex gap-3">
          {canEdit && (
            <Button type="submit" disabled={updateIpRestriction.isPending}>
              保存する
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => navigate("/organizations")}>
            組織管理へ戻る
          </Button>
        </div>
      </form>

      <EnableIpRestrictionModal
        isOpen={confirmingValues !== null}
        organizationName={organizationName}
        allowedIpCount={confirmingValues?.allowedIps.length ?? 0}
        isSubmitting={updateIpRestriction.isPending}
        onConfirm={() => confirmingValues && save(confirmingValues)}
        onClose={() => setConfirmingValues(null)}
      />
    </Form>
  )
}
